import { createContext, ReactNode, use, useMemo, useState } from 'react';
import {
  ClientOrg,
  Hymn,
  hymnHasVocals,
  isMidiFileMissing,
  PracticeVideoPart,
  PracticeVideos,
  SongSlug,
  YouTubeVideoId,
} from '../../../../common/src/model.ts';
import { customSongOrgId } from '../../../../common/src/paths.ts';
import { practiceVideoCount, practiceVideoParts } from '../../../../common/src/youtube.ts';
import { useCurrentOrg } from '../../data/use_current_org.tsx';
import { CurrentSongContext } from '../current_song.tsx';

// Video mode (GitHub issue #4041): the player plays one of the current song's YouTube practice videos
// instead of its piano or vocals. In memory only: an app restart is back to the song's usual sound.
export interface PracticeVideo {
  // the current song's practice videos, from the org that owns it
  practiceVideos: PracticeVideos | undefined;
  hasPracticeVideos: boolean;
  isVideoMode: boolean;
  setVideoMode: (on: boolean) => void;
  // the part playing: the one chosen when it has a video, else the first part that has one, else 'all'
  selectedPart: PracticeVideoPart | undefined;
  setSelectedPart: (part: PracticeVideoPart) => void;
  // the video the sequencer plays; undefined outside video mode
  videoId: YouTubeVideoId | undefined;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}

export const PracticeVideoContext = createContext<PracticeVideo>({
  practiceVideos: undefined,
  hasPracticeVideos: false,
  isVideoMode: false,
  setVideoMode: () => undefined,
  selectedPart: undefined,
  setSelectedPart: () => undefined,
  videoId: undefined,
  playbackRate: 1,
  setPlaybackRate: () => undefined,
});

// a custom song's practice videos live on the org in its slug (church/<orgId>/song/<n>)
function songPracticeVideos({
  slug,
  householdOrgs,
  currentOrg,
}: {
  slug: SongSlug | undefined;
  householdOrgs: ClientOrg[];
  currentOrg: ClientOrg | undefined;
}): PracticeVideos | undefined {
  const orgId = slug ? customSongOrgId(slug) : undefined;
  if (!slug || orgId === undefined) {
    return;
  }
  const org = householdOrgs.find(({ id }) => id === orgId) ?? (currentOrg?.id === orgId ? currentOrg : undefined);
  return org?.practiceVideos?.[slug];
}

// The song's own sounds as its manifest lists them, rather than as the last load went: a download that failed
// offline does not take the piano out of the source oval, nor push the song into video mode
export function songSources(hymn: Hymn | undefined): { hasPiano: boolean; hasVocals: boolean } {
  return { hasPiano: !!hymn && !isMidiFileMissing(hymn), hasVocals: !!hymn && hymnHasVocals(hymn) };
}

function hasAnyPracticeVideo(practiceVideos: PracticeVideos | undefined): boolean {
  const { parts, hasWholeSong } = practiceVideoCount(practiceVideos);
  return parts > 0 || hasWholeSong;
}

// The part that plays: the one asked for when this song has a video for it, else the first part with one,
// else the whole-song video. The request is kept, so a song without that part does not lose it for the next.
function effectivePracticeVideoPart(
  practiceVideos: PracticeVideos | undefined,
  requestedPart: PracticeVideoPart | undefined,
): PracticeVideoPart | undefined {
  if (!hasAnyPracticeVideo(practiceVideos)) {
    return;
  }
  if (requestedPart && practiceVideos?.[requestedPart]) {
    return requestedPart;
  }
  return practiceVideoParts.find((part) => practiceVideos?.[part]) ?? 'all';
}

export const PracticeVideoProvider = ({ children }: { children: ReactNode }) => {
  const { hymn } = use(CurrentSongContext);
  const { householdOrgs, currentOrg } = useCurrentOrg();
  const [videoModeRequested, setVideoModeRequested] = useState(false);
  const [requestedPart, setRequestedPart] = useState<PracticeVideoPart | undefined>();
  const [playbackRate, setPlaybackRate] = useState(1);

  const practiceVideos = useMemo(
    () => songPracticeVideos({ slug: hymn?.slug, householdOrgs, currentOrg }),
    [currentOrg, householdOrgs, hymn?.slug],
  );
  const hasPracticeVideos = hasAnyPracticeVideo(practiceVideos);
  // a song without practice videos leaves video mode, so the next song with them starts in its usual sound
  // (adjusted while rendering rather than in an effect, so no render shows the stale mode)
  if (videoModeRequested && !hasPracticeVideos) {
    setVideoModeRequested(false);
  }
  // a song whose only sound is its practice videos is always in video mode
  const { hasPiano, hasVocals } = songSources(hymn);
  const isVideoTheOnlySource = !hasPiano && !hasVocals;
  const isVideoMode = hasPracticeVideos && (videoModeRequested || isVideoTheOnlySource);
  const selectedPart = effectivePracticeVideoPart(practiceVideos, requestedPart);
  const videoId = isVideoMode && selectedPart ? practiceVideos?.[selectedPart] : undefined;

  const value = useMemo(
    () => ({
      practiceVideos,
      hasPracticeVideos,
      isVideoMode,
      setVideoMode: setVideoModeRequested,
      selectedPart,
      setSelectedPart: setRequestedPart,
      videoId,
      playbackRate,
      setPlaybackRate,
    }),
    [hasPracticeVideos, isVideoMode, playbackRate, practiceVideos, selectedPart, videoId],
  );

  return <PracticeVideoContext value={value}>{children}</PracticeVideoContext>;
};
